/**
 * Helpers for DATE-only values (e.g. Postgres `date` columns).
 *
 * Important: never parse `YYYY-MM-DD` into a JS Date via `new Date(ymd)` because
 * that is interpreted as UTC and can shift the displayed day in local timezones.
 */
export function extractYmd(value: any): string | null {
  if (value === null || value === undefined) return null;

  // If the backend ever returns a Date object, preserve the calendar date by
  // using the UTC date portion (matches how DATE values are typically serialized).
  if (value instanceof Date) {
    if (!Number.isFinite(value.getTime())) return null;
    return value.toISOString().slice(0, 10); // YYYY-MM-DD
  }

  const s = String(value).trim();
  if (!s) return null;

  // Match YYYY-MM-DD at the start (also covers ISO strings like YYYY-MM-DDTHH:mm:ssZ).
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  return `${m[1]}-${m[2]}-${m[3]}`;
}

export function toLocalYmd(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** For `<input type="date" />` values. */
export function toDateInputValue(value: any): string {
  const ymd = extractYmd(value);
  return ymd || '';
}

/** Formats a DATE-only value without timezone shifting. */
export function formatDateOnly(
  value: any,
  locale?: string,
  options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' }
): string {
  const ymd = extractYmd(value);
  if (!ymd) return '—';
  const [yy, mm, dd] = ymd.split('-').map((x) => parseInt(x, 10));
  if (!Number.isFinite(yy) || !Number.isFinite(mm) || !Number.isFinite(dd)) return '—';

  // Construct a *local* date (no UTC parsing), so the day never shifts.
  const d = new Date(yy, mm - 1, dd);
  if (!Number.isFinite(d.getTime())) return '—';
  return d.toLocaleDateString(locale, options);
}

/**
 * Parses a DATE-only value into a local Date (midnight local time).
 * Returns null if value can't be parsed.
 */
export function parseDateOnlyToLocalDate(value: any): Date | null {
  const ymd = extractYmd(value);
  if (!ymd) return null;
  const [yy, mm, dd] = ymd.split('-').map((x) => parseInt(x, 10));
  if (!Number.isFinite(yy) || !Number.isFinite(mm) || !Number.isFinite(dd)) return null;
  const d = new Date(yy, mm - 1, dd);
  return Number.isFinite(d.getTime()) ? d : null;
}

/**
 * Parses either a timestamp/datetime string OR a DATE-only string safely.
 * - DATE-only (YYYY-MM-DD...) is treated as local date (no timezone shift)
 * - otherwise, falls back to JS Date parsing
 */
export function parseDateOrDateTime(value: any): Date | null {
  if (value === null || value === undefined) return null;
  if (value instanceof Date) return Number.isFinite(value.getTime()) ? value : null;
  const s = String(value).trim();
  if (!s) return null;
  if (/^\d{4}-\d{2}-\d{2}(?:$|[T\s])/.test(s)) {
    // If it starts with YYYY-MM-DD, treat as date-only (even if it has time appended)
    // because our invoice DATE fields may come back as YYYY-MM-DD or YYYY-MM-DDT00:00:00...
    const d0 = parseDateOnlyToLocalDate(s);
    if (d0) return d0;
  }
  const d = new Date(s);
  return Number.isFinite(d.getTime()) ? d : null;
}

